'use client'

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { LedgerStateChip } from './PayoutLedger'
import {
  displayedCommissionRubles,
  formatDayMonth,
  formatFullDate,
  formatPeriod,
  formatRubles,
  formatShare,
  inclusiveDays,
  payoutShareBasisPoints,
  pluralOrders,
  pluralRu,
  SALES_PAYOUT_DIFFERENCE,
  type MarketMoneyLedgerRow,
} from '@/lib/market-money'

const REPORT_BREAKDOWN_LINES = ['Продажи', 'Бонусы и компенсации Маркета', 'Комиссия Маркета', 'Доставка и услуги'] as const
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function Timeline({ row }: { row: MarketMoneyLedgerRow }) {
  const deliveryDays = inclusiveDays(row.deliveryFrom, row.deliveryTo)
  const totalDays = Math.max(1, deliveryDays + Math.max(0, row.lagDays))
  const deliveryWidth = (deliveryDays / totalDays) * 94
  const received = row.state === 'received'
  return (
    <div className="relative h-[54px]">
      <div className="absolute inset-x-0 top-3.5 h-0.5 bg-rule" />
      <div className="absolute left-0 top-2 h-3.5 bg-gold-soft" style={{ width: `${deliveryWidth}%` }} />
      <div
        className="absolute top-3.5 h-0.5"
        style={{ left: `${deliveryWidth}%`, width: `${94 - deliveryWidth}%`, background: 'repeating-linear-gradient(90deg, var(--color-gold) 0 6px, transparent 6px 10px)' }}
      />
      <div className={`absolute top-2 size-3.5 rounded-full ${received ? 'bg-st-delivered' : 'border-2 border-gold bg-paper'}`} style={{ left: 'calc(94% - 7px)' }} />
      <div className="tab-nums absolute left-0 top-[30px] text-[12px] text-ink-2">{formatPeriod(row.deliveryFrom, row.deliveryTo)} · доставки</div>
      <div className="absolute left-1/2 top-[30px] hidden -translate-x-1/2 text-[12px] text-ink-faint sm:block">через {pluralRu(row.lagDays, ['день', 'дня', 'дней'])}</div>
      <div className={`tab-nums absolute right-0 top-[30px] text-[12px] font-semibold ${received ? 'text-received' : 'text-gold-deep'}`}>{formatDayMonth(row.payoutDate)} · выплата</div>
    </div>
  )
}

export function PayoutDetailDrawer({ row, onClose, returnFocusTo = null }: {
  row: MarketMoneyLedgerRow
  onClose: () => void
  returnFocusTo?: HTMLElement | null
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)
  const returnFocusRef = useRef(returnFocusTo)

  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onCloseRef.current() }
    window.addEventListener('keydown', onKeyDown)
    const restoreTarget = returnFocusRef.current
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      restoreTarget?.focus()
    }
  }, [])

  function trapFocus(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key !== 'Tab' || !dialogRef.current) return
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const payoutShare = row.commissionKopecks === null ? null : payoutShareBasisPoints(row.salesKopecks, row.payoutKopecks)
  const commissionRubles = row.commissionKopecks === null ? null : displayedCommissionRubles(row.salesKopecks, row.payoutKopecks)

  return (
    <div className="fixed inset-0 z-[80] flex justify-end">
      <div data-testid="payout-drawer-backdrop" className="mission-drawer-backdrop absolute inset-0 bg-onyx/30" aria-hidden="true" onClick={() => onCloseRef.current()} />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Выплата Маркета ${formatDayMonth(row.payoutDate)}`}
        onKeyDown={trapFocus}
        className="mission-drawer-enter relative flex h-full w-full max-w-[720px] flex-col overflow-y-auto border-l border-rule-strong bg-paper"
      >
        <div className="flex flex-col gap-2.5 border-b border-rule px-5 py-[22px] sm:px-[26px]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="eyebrow">Выплата Маркета</span>
              <LedgerStateChip state={row.state} />
            </div>
            <button ref={closeRef} type="button" onClick={() => onCloseRef.current()} aria-label="Закрыть" className="grid size-8 place-items-center text-ink-muted hover:text-ink">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" /></svg>
            </button>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
            <span className="tab-nums whitespace-nowrap text-[32px] font-semibold tracking-[-0.02em] sm:text-[38px]">{formatRubles(row.payoutKopecks)}</span>
            <span className="tab-nums text-[16px] text-ink-muted">{formatFullDate(row.payoutDate)}</span>
          </div>
          <div className="text-[13px] text-ink-muted">За доставки {formatPeriod(row.deliveryFrom, row.deliveryTo)}.{row.deliveryTo.slice(0, 4)} · {pluralOrders(row.orders)}</div>
        </div>

        <div className="flex flex-col gap-2.5 border-b border-rule px-5 py-5 sm:px-[26px]">
          <div className="eyebrow">Сроки</div>
          <Timeline row={row} />
        </div>

        <div className="flex flex-col gap-1 px-5 py-5 sm:px-[26px]">
          <div className="eyebrow mb-2">Расчёт</div>
          <div className="flex justify-between border-b border-rule py-2.5">
            <span>Продажи</span><span className="tab-nums whitespace-nowrap">{formatRubles(row.salesKopecks)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3 border-b border-rule py-2.5">
            <span className="flex flex-col gap-0.5">
              <span>{SALES_PAYOUT_DIFFERENCE.label}</span>
              {commissionRubles === null
                ? <span className="text-[12px] text-ink-faint">посчитаем, когда неделя закончится</span>
                : SALES_PAYOUT_DIFFERENCE.caption && <span className="text-[12px] text-ink-faint">{SALES_PAYOUT_DIFFERENCE.caption}</span>}
            </span>
            <span data-testid="payout-drawer-commission" className="tab-nums whitespace-nowrap text-st-unpaid">
              {commissionRubles === null
                ? '—'
                : `${commissionRubles >= 0 ? '−' : '+'} ${Math.abs(commissionRubles).toLocaleString('ru-RU')} ₽`}
            </span>
          </div>
          <div className="flex justify-between border-b-2 border-ink py-3 font-semibold">
            <span>Выплата Маркета</span>
            <span className="tab-nums whitespace-nowrap">{formatRubles(row.payoutKopecks)}{payoutShare !== null ? ` · ${formatShare(payoutShare, 0)}` : ''}</span>
          </div>

          <div className="eyebrow mb-2 mt-[22px]">Детализация из отчёта Маркета</div>
          {REPORT_BREAKDOWN_LINES.map((label) => (
            <div key={label} className="flex items-center justify-between border-b border-dashed border-rule py-[9px] text-[13px] text-ink-faint">
              <span>{label}</span>
              <span className="h-2.5 w-[120px] bg-bone" aria-hidden="true" />
            </div>
          ))}
          <div className="mt-2 text-[12px] leading-normal text-ink-muted">Появится, когда начнём сохранять отчёт Маркета построчно.</div>
        </div>
      </aside>
    </div>
  )
}
